import { log, toHexString } from './LogUtil'
import { concatTypedArray, indexOfSubarrayFast, crc16, decodeBase64Uint8Array } from './CommonUtil'
import { decode, encode } from 'cbor2'

function dataReceiveCallback(data: Uint8Array): void {
  log.info(`dataReceived:${toHexString(data, ',0x')}`)
}

const smp_first_header = new Uint8Array([0x06, 0x09])
const smp_continuation_header = new Uint8Array([0x04, 0x14])

let SerialData: Uint8Array = new Uint8Array(0)
let SMPBuffer: Uint8Array = new Uint8Array(0)

let waiting_packet_length = 0
let SMPWaitingForContinuation = false

let SMPBufferActualData: Uint8Array = new Uint8Array(0)

export function processSMPPacket(
  data: Uint8Array,
  dataReceived = dataReceiveCallback,
  onError?: (error: string) => void,
): void {
  SerialData = concatTypedArray(SerialData, data)
  let pos = indexOfSubarrayFast(SerialData, smp_first_header)
  let posA = SerialData.indexOf(0x0a, pos + 2)

  let pos_other = indexOfSubarrayFast(SerialData, smp_continuation_header)
  let posA_other = SerialData.indexOf(0x0a, pos_other + 2)

  while ((pos !== -1 && posA !== -1) || (pos_other !== -1 && posA_other !== -1)) {
    if (pos >= 0 && (pos_other === -1 || pos < pos_other)) {
      SMPBuffer = new Uint8Array(0)

      SMPBuffer = new Uint8Array(SerialData.slice(pos + 2, posA))
      SMPBuffer = decodeBase64Uint8Array(SMPBuffer)
      if (SMPBuffer.length === 0) {
        log.error('Failed decoding base64')
      } else if (SMPBuffer.length > 2) {
        waiting_packet_length = (SMPBuffer[0] << 8) | (SMPBuffer[1] & 0xff)
        SMPBuffer = new Uint8Array(SMPBuffer.slice(2))

        if (SMPBuffer.length >= waiting_packet_length) {
          const realData = new Uint8Array(SMPBuffer.slice(0, -2))
          const crc = crc16(realData, 0x1021, 0, true)
          const msgCrc =
            (SMPBuffer[SMPBuffer.length - 2] << 8) | (SMPBuffer[SMPBuffer.length - 1] & 0xff)
          if (crc === msgCrc) {
            SMPBuffer = realData
            dataReceived(SMPBuffer)
          } else {
            const errMsg = `CRC mismatch: expected ${msgCrc.toString(16)}, got ${crc.toString(16)}`
            log.error(errMsg)
            onError?.(errMsg)
          }
        } else {
          SMPWaitingForContinuation = true
          SMPBufferActualData = SMPBuffer
        }
      }
      SerialData = new Uint8Array(SerialData.slice(posA + 1))
      pos = indexOfSubarrayFast(SerialData, smp_first_header)
      posA = SerialData.indexOf(0x0a, pos + 2)
      pos_other = indexOfSubarrayFast(SerialData, smp_continuation_header)
      posA_other = SerialData.indexOf(0x0a, pos_other + 2)
    } else if (SMPWaitingForContinuation) {
      SMPBuffer = new Uint8Array()
      SMPBuffer = new Uint8Array(SerialData.slice(pos_other + 2, posA_other))
      SMPBuffer = decodeBase64Uint8Array(SMPBuffer)
      if (SMPBuffer.length === 0) {
        log.error('Failed decoding base64')
      }
      if (SMPBuffer.length >= 0) {
        SMPBufferActualData = new Uint8Array(concatTypedArray(SMPBufferActualData, SMPBuffer))

        if (SMPBufferActualData.length >= waiting_packet_length) {
          const realData = new Uint8Array(SMPBufferActualData.slice(0, -2))
          const crc = crc16(realData, 0x1021, 0, true)
          const msgCrc =
            (SMPBufferActualData[SMPBufferActualData.length - 2] << 8) |
            (SMPBufferActualData[SMPBufferActualData.length - 1] & 0xff)

          if (crc === msgCrc) {
            SMPBufferActualData = realData
            dataReceived(SMPBufferActualData)
          } else {
            const errMsg = `CRC mismatch: expected ${msgCrc.toString(16)}, got ${crc.toString(16)}`
            log.error(errMsg)
            onError?.(errMsg)
          }
          SMPBufferActualData = new Uint8Array()
          SMPWaitingForContinuation = false
        } else {
          SMPWaitingForContinuation = true
        }
      }
      SerialData = SerialData.slice(posA_other + 1)
      pos = indexOfSubarrayFast(SerialData, smp_first_header)
      if (pos === -1) {
        posA = -1
      } else {
        posA = SerialData.indexOf(0x0a, pos + 2)
      }

      pos_other = indexOfSubarrayFast(SerialData, smp_continuation_header)
      if (pos_other === -1) {
        posA_other = -1
      } else {
        posA_other = SerialData.indexOf(0x0a, pos_other + 2)
      }
    }
  }
  if (
    SerialData.length > 10 &&
    indexOfSubarrayFast(SerialData, smp_first_header) === -1 &&
    indexOfSubarrayFast(SerialData, smp_continuation_header) === -1
  ) {
    log.error('Cleared garbage data in UART SMP transport buffer')
    SerialData = new Uint8Array(0)
  }
}

export interface SmpHeader {
  nh_op: number
  nh_version: number
  _res1: number
  nh_flags: number
  nh_len: number
  nh_group: number
  nh_seq: number
  nh_id: number
}

export function get_header(buffer: Uint8Array): SmpHeader | null {
  if (buffer.length < 8) {
    // 检查长度是否足够
    return null
  }
  buffer = buffer.slice(0, 8) as Uint8Array

  const dataView = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength)
  const byte0 = dataView.getUint8(0)

  // 解析小端位域布局：nh_op(3位), nh_version(2位), _res1(3位)
  const nh_op = byte0 & 0x07 // 低3位
  const nh_version = (byte0 >> 3) & 0x03 // 中间2位
  const _res1 = (byte0 >> 5) & 0x07 // 高3位

  let nh_group = dataView.getUint16(4, true)
  nh_group = ((nh_group & 0xff) << 8) | ((nh_group & 0xff00) >> 8)
  return {
    nh_op, // 数值对应C++枚举，如0=SMP_OP_READ
    nh_version,
    _res1,
    nh_flags: dataView.getUint8(1),
    nh_len: dataView.getUint16(2, true), // 小端读取uint16
    // nh_group: dataView.getUint16(4, true),
    nh_group: nh_group,
    nh_seq: dataView.getUint8(6),
    nh_id: dataView.getUint8(7),
  }
}

export function getContent(buffer: Uint8Array): Uint8Array | null {
  if (buffer.length < 8) {
    // 检查长度是否足够
    return null
  }
  return buffer.slice(8) as Uint8Array
}

export function decodeCommon(buffer: Uint8Array): unknown {
  return decode(buffer)
}

export function encodeCommon(buffer: unknown): Uint8Array {
  return encode(buffer) as Uint8Array
}
